import AppCommandBar from 'app/AppCommandBar'
import { AccountContext } from 'auth'
import { FlexSpinner } from 'components'
import { StickyPane } from 'components/StickyPane'
import { WikiForm } from 'forms/WikiForm/WikiForm'
import { useCustomSearchCommand, useEditCommand } from 'hooks/useCommand'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import { useSelection } from 'hooks/useSelection'
import { CheckType, Wiki, WikiUpdateInput } from 'model'
import {
  IStackTokens,
  SelectionMode,
  SelectionZone,
  Stack,
} from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import { useUpdateWikiCheckMutation } from 'views/AdminWikiView/AdminWikiView.generated'
import { WikiListSingleView } from 'views/AdminWikiView/WikiListSingleView'
import { useReadWikisQuery } from './WikiView.generated'

export const WikiView: React.FC = () => {
  const { t } = useTranslation()
  const [panelState, showPanel] = useFormikPanelState()
  const [selection, selectedItems] = useSelection<Wiki>({
    getKey: (item) => item._id,
  })

  const [routeParams, updateRouteParams] = useRouteParams('admin.data.wiki', {
    search: undefined,
  })

  const router = useRouter()
  const paramsNew = router.getState()
  const newElementsCount = paramsNew.params.wikiCounter || 0
  const [updateWikiCheck] = useUpdateWikiCheckMutation()

  const currentMicrosoftId: { homeAccount: string } =
    React.useContext(AccountContext)
  // const modifiedWikisCounter = useNewWikiContext()
  React.useEffect(() => {
    const updateAsync = async () => {
      await updateWikiCheck({
        variables: {
          update: {
            user: currentMicrosoftId.homeAccount,
            type: CheckType.Wiki,
          },
        },
      })
    }
    updateAsync()
    // modifiedWikisCounter.setRefetchFlag(oldFlag => !oldFlag)
    // WARNING! Do NOT put modifiedWikisCounter in dependency array.
    // Page will do infinite requests when you do this!
  }, [currentMicrosoftId.homeAccount, updateWikiCheck])

  const { loading, data, error } = useReadWikisQuery({
    variables: { search: routeParams.search },
  })
  const wikisUnsorted: Wiki[] =
    !loading && data && data.wikis ? (data.wikis as Wiki[]) : []
  const wikis = wikisUnsorted.sort((a: Wiki, b: Wiki) => {
    const firstDate = a.created! as Date
    const secondDate = b.created! as Date
    const result = firstDate > secondDate ? -1 : firstDate < secondDate ? 1 : 0
    return result
  })
  const wikiList = wikis.map((elem, index) => {
    return { ...elem, key: `${elem._id}` }
  })

  selection.setItems(wikiList, false)

  const verticalGapStackTokens: IStackTokens = {
    childrenGap: '2vh',
    padding: '1vh',
  }

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.wiki.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => updateRouteParams({ search: undefined }),
    },
    [t, routeParams, updateRouteParams]
  )

  const handleItemEdit = () => {
    const item = selectedItems[0]
    showPanel({
      ...selectedItems[0],
      provingGround: item.provingGround ? item.provingGround._id : undefined,
    } as WikiUpdateInput)
  }
  useEditCommand(
    { name: 'wiki.viewUser', count: selectedItems.length },
    {
      priority: 20,
      onClick: handleItemEdit,
    },
    [selectedItems]
  )
  return (
    <StickyPane>
      <FlexSpinner loading={loading} error={error}>
        <AppCommandBar />

        <SelectionZone selection={selection}>
          <Stack wrap={true} horizontal={true} tokens={verticalGapStackTokens}>
            {wikiList.map((wikiElement, index) => (
              <WikiListSingleView
                newElement={index < newElementsCount ? true : false}
                key={wikiElement.key}
                item={wikiElement}
                itemIndex={index}
                selection={selection}
                selectionMode={SelectionMode.single}
                onShowHandler={handleItemEdit}
              />
            ))}
          </Stack>
        </SelectionZone>

        <FormikPanel
          {...panelState}
          onSubmit={() => undefined}
          headerText={`${t('model.wiki.view')}`}
          enableSubmit={false}
        >
          <WikiForm disabled={true} />
        </FormikPanel>
      </FlexSpinner>
    </StickyPane>
  )
}
